import { useState, useEffect, useCallback } from 'react'

interface UseCountUpOptions {
  duration?: number
  startOnMount?: boolean
  delay?: number
}

export function useCountUp(
  endValue: number,
  options: UseCountUpOptions = {}
) {
  const {
    duration = 1000,
    startOnMount = true,
    delay = 0
  } = options

  const [count, setCount] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  const animate = useCallback(() => {
    const startTime = Date.now()
    const startValue = 0

    const runAnimation = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutQuart)

      setCount(currentValue)

      if (progress < 1) {
        requestAnimationFrame(runAnimation)
      } else {
        setCount(endValue)
        setIsAnimating(false)
      }
    }

    setIsAnimating(true)
    requestAnimationFrame(runAnimation)
  }, [endValue, duration])

  const start = useCallback(() => {
    setCount(0)
    animate()
  }, [animate])

  useEffect(() => {
    if (!startOnMount) return

    const timer = setTimeout(animate, delay)
    return () => clearTimeout(timer)
  }, [startOnMount, delay, animate])

  return { count, isAnimating, start }
}
